// 버핏스코어 — Claude Design "Valuation Agent v3" 시안(2026-08-14) 이식.
//
// 화면: 좌 360 목록(제목·검색·저장된 분석) / 우 본문 3상태
//   랜딩(분석 이력이 선택되지 않았을 때) · 분석 중(로그) · 검토 조서(결과)
// 흐름은 이전과 같다: 종목 검색(KR 자동완성 / US 티커) → (US 면 10-K enrichment)
//   → /buffett/analyze async → 폴링(step 타임라인) → 검토 조서 + PDF.
// 시안에 있는 "근거 지표(수치 타일)"·"확인이 필요한 지점(리스크 목록)"은 백엔드
// 응답에 대응 필드가 없다 → 실제로 오는 정성 분석 7개 섹션과 미평가 해자 축으로
// 같은 자리를 채운다 (없는 수치를 지어내지 않는다).
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import LiveSheet, { buffettFinalLines, useLiveSheet } from "../components/lab/LiveSheet";
import {
  buffettCancel, buffettPdf, buffettRating, buffettScore100, buffettSnapshot, buffettStart, buffettStatusPath, buffettSummary, buffettTier,
  buffettUs10k, elapsedLabel, pollTask, searchCompany,
  type BuffettAnalysis, type CompanyHit, type PollHandle,
} from "../lib/labApi";
import { buffettRecentList, recordBuffettRecent, type BuffettRecent } from "../lib/buffettRecent";
import { recordLabFile } from "../lib/labFiles";
import { labHeartbeat, labSet, labStopHeartbeat, useLabState } from "../lib/labState";
import { libDownload, libViewUrl } from "../lib/library";
import { searchUS } from "../lib/usTicker";
import { SheetDim, SheetGrabber } from "../ui/Sheet";
import { Chip } from "../ui/Chip";
import { PanelClose, SidePanel } from "../ui/SidePanel";
import { RadarChart } from "../ui/RadarChart";
import { CompanyLogo } from "../ui/CompanyLogo";
// 초상은 번들 자산으로 둔다 — 서버가 정적으로 서빙하는 경로는 /assets/* 뿐이라
// public/ 에 두면 배포 환경에서 404 가 난다. 둘 다 360² WebP 로 최적화했다.
import portrait from "../assets/buffett.webp";
import { buffettPortrait } from "../lib/buffettPortrait";
import "../styles/lab.css";
import "../styles/buffett.css";

/** 서버 분석 캐시 수명 — 이 안이면 저장된 결과를 그대로 보여주고, 넘으면 새로 분석한다 */
const CACHE_DAYS = 7;

/** 재생 도중 사용자가 중단했음을 알리는 신호 — 오류로 표시하지 않는다. */
class ReplayAborted extends Error {}

const MOAT_LABELS: Record<string, string> = {
  costAdvantage: "원가 우위", brandPower: "브랜드", switchingCost: "전환 비용",
  networkEffect: "네트워크 효과", scaleEconomics: "규모의 경제", regulatoryBarrier: "규제 장벽",
};

const SECTION_LABELS: Record<string, string> = {
  revenueConcentration: "매출 집중도", managementOwnership: "경영진 지분",
  managementTurnover: "경영진 안정성", customerRetention: "고객 유지율",
  productNecessity: "제품 필수성", regulatoryEnvironment: "규제 환경",
  competitivePosition: "경쟁 포지션",
};

// 랜딩 "채점 방식" 4장 — 시안 카피 그대로
const HOW: { t: string; s: string; icon: ReactNode }[] = [
  {
    t: "경제적 해자 6대 축",
    s: "원가 우위, 브랜드, 전환 비용, 네트워크 효과, 규모의 경제, 규제 장벽을 각 10점으로 채점합니다.",
    icon: <><path d="M4 20V9l8-5 8 5v11" /><path d="M9 20v-6h6v6" /></>,
  },
  {
    t: "7대 투자 게이트",
    s: "이해 가능한 사업, 장기 경제성, 정직한 경영진, 높은 자기자본이익률 등 일곱 조건의 통과 여부를 판정합니다.",
    icon: <path d="M20 6L9 17l-5-5" />,
  },
  {
    t: "버핏 1인칭 코멘트",
    s: "그가 이 회사를 두고 할 법한 판단을 한 문단으로 정리하고, 확인이 필요한 지점을 남깁니다.",
    icon: <path d="M21 12a8 8 0 0 1-8 8H4l2-3a8 8 0 1 1 15-5z" />,
  },
  {
    t: "한국·미국 상장사",
    s: "국내는 사업보고서, 미국은 SEC 10-K 사업내용을 읽고 한 번에 한 종목씩 분석합니다.",
    icon: <><path d="M7 4h7l4 4v12H7z" /><path d="M14 4v4h4" /><path d="M10 13h5" /><path d="M10 16h5" /></>,
  },
];

// 버크셔 해서웨이 보유 종목 — 2026년 2분기 13F(2026-08-14 SEC 제출, 기준일 2026-06-30) 상위 10종목을
// 포트폴리오 비중순으로. 랜딩의 빠른 진입. 분기마다(2·5·8·11월 중순 제출) 갱신한다.
// 비중(Q2): Apple 22.0 · AmEx 17.1 · Alphabet 12.6(A 9.4+C 3.2) · Coca-Cola 10.9 · BofA 9.2 · Chevron 4.7
//          · Occidental 4.3 · Chubb 3.9 · Moody's 3.7 · Kraft Heinz 2.6 (%)
const BERKSHIRE: { name: string; ticker: string; since: string; why: string }[] = [
  { name: "Apple", ticker: "NASDAQ:AAPL", since: "2016년", why: "생태계 전환 비용이 만드는 반복 구매. 버크셔 주식 포트폴리오의 최대 비중" },
  { name: "American Express", ticker: "NYSE:AXP", since: "1991년", why: "가맹점과 회원이 서로를 끌어당기는 결제망, 프리미엄 회원 기반" },
  { name: "Alphabet", ticker: "NASDAQ:GOOGL", since: "2025년", why: "검색·유튜브 광고와 클라우드. 2분기에 A·C주를 크게 늘려 4번째 비중이 됐다" },
  { name: "Coca-Cola", ticker: "NYSE:KO", since: "1988년", why: "38년째 보유. 브랜드와 세계 유통망이 만드는 가격 결정력" },
  { name: "Bank of America", ticker: "NYSE:BAC", since: "2011년", why: "저비용 예금 기반과 미국 소매금융 점유율. 2분기에도 일부 매도" },
  { name: "Chevron", ticker: "NYSE:CVX", since: "2020년", why: "저비용 매장량과 배당 이력, 에너지 가격 상승기의 현금 창출" },
  { name: "Occidental Petroleum", ticker: "NYSE:OXY", since: "2019년", why: "퍼미안 분지 자산 집중, 우선주와 워런트를 함께 보유" },
  { name: "Chubb", ticker: "NYSE:CB", since: "2023년", why: "언더라이팅 규율이 만드는 손해보험 이익. 버크셔 본업(보험)과 같은 원리" },
  { name: "Moody's", ticker: "NYSE:MCO", since: "2000년", why: "신용평가 2社 과점 구조. 자본이 거의 들지 않는 사업" },
  { name: "Kraft Heinz", ticker: "NASDAQ:KHC", since: "2015년", why: "브랜드 식품 포트폴리오. 버핏이 인수 가격을 과했다고 인정한 사례" },
];

type Sel = { name: string; symbol: string; market: "kr" | "us" };

// 히어로 검색 아래 빠른 선택 — 검색어를 떠올리기 전에 누를 것을 준다
const QUICK: Sel[] = [
  { name: "삼성전자", symbol: "KRX:005930", market: "kr" },
  { name: "SK하이닉스", symbol: "KRX:000660", market: "kr" },
  { name: "현대차", symbol: "KRX:005380", market: "kr" },
  { name: "AAPL", symbol: "AAPL", market: "us" },
  { name: "MSFT", symbol: "MSFT", market: "us" },
  { name: "NVDA", symbol: "NVDA", market: "us" },
];

/**
 * 코멘트 옆 초상 — 점수가 기준(60점, '훌륭한 기업' 경계이자 점수 색이 바뀌는
 * 지점) 아래면 갸웃하는 쪽을 쓴다. 랜딩 히어로는 점수가 없으므로 기본 초상.
 */
/** 점수 색 — labApi.buffettTier 와 같은 구간(80/60/40) */
function scoreClass(score: number): string {
  const tier = buffettTier(score);
  return tier === "good" ? "bfx-good" : tier === "mid" ? "bfx-mid" : "bfx-bad";
}

/** 검색 결과 한 건을 실행 대상으로 정규화 — KRX/KOSDAQ 접두사가 없으면 미국 티커 */
function toSel(h: CompanyHit): Sel {
  const isUs = !/^(KRX|KOSDAQ):/.test(h.symbol);
  const ticker = h.symbol.replace(/^[A-Za-z]+:/, "");
  const name = isUs ? h.name.replace(/ \([A-Z.-]+\)$/, "") || ticker : h.name;
  return { name, symbol: isUs ? ticker : h.symbol, market: isUs ? "us" : "kr" };
}

export const analysisDate = (r?: BuffettAnalysis | null) =>
  String((r as { analysisDate?: string } | null)?.analysisDate
    || new Date().toISOString().slice(0, 10));

// 만들어 둔 PDF 가 지금 화면의 것인지 가리는 열쇠. 같은 기업이라도 분석일이
// 다르면 다른 보고서다(캐시 만료 후 재분석).
export const pdfOwner = (sel: Sel, r: BuffettAnalysis) => `${sel.symbol}|${analysisDate(r)}`;

export default function BuffettPage() {
  // 조회 상태는 labState 로 승격 — 다른 페이지로 나가도 분석이 계속 돌고,
  // 돌아오면 작동 중인 화면이 그대로 복원된다 (채팅 P1b 백그라운드 스트림과 동일 원리).
  const [query, setQuery] = useLabState("bf.query", "");
  const [hits, setHits] = useState<CompanyHit[]>([]);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useLabState<Sel | null>("bf.picked", null);
  const [running, setRunning] = useLabState("bf.running", false);
  const [steps, setSteps] = useLabState<string[]>("bf.steps", []);
  const [startedAt, setStartedAt] = useLabState("bf.startedAt", 0);
  const [elapsed, setElapsed] = useState("00:00");
  const [result, setResult] = useLabState<BuffettAnalysis | null>("bf.result", null);
  const [err, setErr] = useLabState("bf.err", "");
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfUrl, setPdfUrl] = useLabState("bf.pdfUrl", "");
  // 만들어 둔 PDF 의 주인 — '기업|분석일'. 경로마다 pdfUrl 을 지우는 걸 기억하는
  // 대신, 주인이 다르면 재사용하지 않게 한다. 딥링크 스냅샷 복원(openSaved →
  // finishWithResult)이 pdfUrl 을 안 지워서, NVDA 를 보다 MSFT 로 옮겨도 NVDA
  // 보고서가 그대로 떴다(사용자 제보).
  const [pdfKey, setPdfKey] = useLabState("bf.pdfKey", "");
  const [pdfOpen, setPdfOpen] = useLabState("bf.pdfOpen", false);
  const [saved, setSaved] = useState<BuffettRecent[]>(() => buffettRecentList());
  // 랜딩에는 히어로·좌측 두 검색이 같은 query 를 공유한다 — 자동완성은 지금
  // 치고 있는 쪽에만 띄운다 (양쪽에 동시에 뜨면 어느 것을 고르는지 모호하다)
  const [typing, setTyping] = useState<"side" | "hero">("hero");
  const pollRef = useRef<PollHandle | null>(null);
  // 중단 버튼이 서버에 보낼 task_id. 폴링만 끊으면 서버 작업은 계속 돌아
  // 캐시에 저장되므로, 중단은 반드시 서버까지 가야 한다.
  const taskRef = useRef<string>("");
  // 실행 세대. stop() 은 그 순간 존재하는 폴링만 끊을 수 있는데, run() 이
  // buffettStart 응답을 기다리는 중이면 끊을 폴링이 아직 없다. 그대로 두면
  // await 가 끝난 뒤 폴링이 새로 걸려 '중단했는데 잠시 뒤 결과가 뜬다'.
  // 중단할 때 세대를 올리고, await 뒤마다 내 세대가 아직 유효한지 본다.
  const runIdRef = useRef(0);
  const sheet = useLiveSheet("bf.sheet");
  const navigate = useNavigate();

  // 사이드바 완료 점(bf.done) — 페이지를 보고 있으면 완료 즉시 끈다
  useEffect(() => {
    if (!running) labSet("bf.done", false);
  }, [running, result]);

  useEffect(() => {
    document.title = "버핏스코어 · Valuation Agent";
    return () => { document.title = "Valuation Agent · DeepSearch"; };
  }, []);

  // 문서함/공유 딥링크(?symbol=…) 진입 — 같은 종목 결과가 이미 떠 있으면 그대로,
  // 아니면 캐시에서 즉시 복원한다 (7일 캐시라 대부분 순간 완료)
  const { search } = useLocation();
  useEffect(() => {
    const p = new URLSearchParams(search);
    const symbol = p.get("symbol");
    if (!symbol || running) return;
    const sel: Sel = {
      name: p.get("name") || symbol,
      symbol,
      market: (p.get("market") === "us" ? "us" : "kr") as "kr" | "us",
    };
    if (picked?.symbol === sel.symbol && result) return;
    setPicked(sel);
    setQuery(sel.name);
    setHits([]);
    const asof = p.get("asof");
    if (asof) {
      // 생성일 스냅샷 — 7일 캐시 만료와 무관하게 그날의 결과를 그대로 복원
      void buffettSnapshot(sel.name, asof).then((snap) => {
        if (snap) finishWithResult(snap, sel);
        else void run({ sel, instant: true });   // 스냅샷 없으면 라이브 조회
      });
      return;
    }
    void run({ sel, instant: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // 지금 화면의 분석이 바뀌면 열려 있는 PDF 패널도 즉시 비운다. openPdf 의
  // 열쇠 검사만으로는, 패널을 열어 둔 채 다른 기업으로 옮겼을 때 이전 보고서가
  // 그대로 보인다 — 누르기 전까지 openPdf 가 돌지 않으므로.
  const pdfOwnerNow = picked && result ? pdfOwner(picked, result) : "";
  useEffect(() => {
    if (pdfKey && pdfKey !== pdfOwnerNow) { setPdfUrl(""); setPdfKey(""); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfOwnerNow]);

  // 검색 자동완성 — KR 은 유사도순 API, US 는 officejs 매퍼(한글명 '애플' → AAPL
  // 포함)를 로컬에서 병합해 상위 10개. 어느 쪽이든 목록에서 눌러 선택해야 한다.
  useEffect(() => {
    const kw = query.trim();
    if (kw.length < 1 || picked) { setHits([]); setSearching(false); return; }
    // 타이핑을 멈추기 전부터 '찾는 중'을 띄운다 — 서버 왕복이 있어 빈 화면이
    // 길게 남으면 검색이 안 되는 것처럼 보인다
    setSearching(true);
    let alive = true;
    const t = setTimeout(() => {
      const us = searchUS(kw, 6).map((r) => ({ symbol: r.company.t, name: r.company.n }));
      if (kw.length < 2) { setHits(us.slice(0, 8)); setSearching(false); return; }
      // 검색창은 사실상 필터다 — 같은 계열사가 여럿 걸리므로 국내 후보를 넉넉히 남긴다
      void searchCompany(kw).then((items) => {
        // 늦게 도착한 이전 질의의 응답이 최신 결과를 덮지 않게 한다
        if (!alive) return;
        // 미국 종목은 두 소스에 다 있다 — 서버 검색은 "IOT"(Samsara Inc.),
        // 로컬 매퍼는 "NYSE:IOT"(삼사라). toSel 이 같은 대상으로 정규화하므로
        // 거래소 접두를 뗀 티커 키로 중복 제거하되, 표기는 접두 있는 쪽을
        // 남긴다(자리는 먼저 온 순서 유지) — 사용자 지정 2026-08-20.
        const key = (s: string) => s.replace(/^[A-Za-z]+:/, "").toUpperCase();
        const prefixed = (s: string) => /^[A-Za-z]+:/.test(s);
        const merged: CompanyHit[] = [];
        const at = new Map<string, number>();
        for (const h of [...items.slice(0, 8), ...us]) {
          const k = key(h.symbol);
          const i = at.get(k);
          if (i === undefined) { at.set(k, merged.length); merged.push(h); }
          else if (!prefixed(merged[i].symbol) && prefixed(h.symbol)) merged[i] = h;
        }
        setHits(merged.slice(0, 12));
        setSearching(false);
      }).catch(() => { if (alive) setSearching(false); });
    }, 300);
    return () => { alive = false; clearTimeout(t); };
  }, [query, picked]);

  // 경과 타이머
  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => setElapsed(elapsedLabel(startedAt)), 1000);
    return () => clearInterval(t);
  }, [running, startedAt]);

  // 언마운트해도 폴링·리플레이를 죽이지 않는다 — labState 에 계속 기록되고,
  // 페이지로 돌아오면 진행 중 화면이 복원된다.

  const pushStep = (msg: string) => {
    setSteps((prev) => {
      if (prev[prev.length - 1] === msg) return prev;
      // 진행 단계를 조서에도 한 줄씩 — 분석이 살아 있음을 보여준다
      sheet.append([{ kind: "prog", cells: ["진행", msg] }]);
      return [...prev, msg].slice(-30);
    });
  };

  // 캐시 적중이라도 결과를 즉시 쏟지 않고 10~20초에 걸쳐 조서를 순서대로 다시 편다
  // (사용자 요청 — 검토 과정이 보여야 결과를 신뢰할 수 있다).
  const replayCached = async (r: BuffettAnalysis, sel: Sel, stale?: () => boolean) => {
    // delay 로 12초 넘게 도는 재생이라 중단을 여기서도 봐야 한다. 폴링만 끊으면
    // 캐시 적중 종목은 중단 뒤에도 재생이 끝까지 가서 결과가 떠버린다(실측).
    const delay = async (ms: number) => {
      await new Promise((res) => setTimeout(res, ms));
      if (stale?.()) throw new ReplayAborted();
    };
    const moats = Object.entries(r.moatAssessment || {});
    pushStep(`최근 ${CACHE_DAYS}일 내 ${sel.name} 분석 이력 확인 — 저장된 검토 조서를 다시 폅니다`);
    await delay(1400);
    pushStep("경제적 해자 6대 축 채점을 검토하는 중…");
    sheet.append([
      { kind: "title", cells: ["[채점] 경제적 해자 6대 축"] },
      { kind: "head", cells: ["항목", "점수", "근거"] },
    ]);
    for (const [k, m] of moats) {
      await delay(1100);
      const label = MOAT_LABELS[k] || k;
      sheet.append([{ kind: "row", cells: [label, `${m?.score ?? "–"}/10`, (m?.evidence || "").slice(0, 80)] }]);
      pushStep(`해자 검토 — ${label}: ${m?.score ?? "–"}/10`);
    }
    await delay(1100);
    pushStep("정성 분석 7개 섹션(매출 집중도·경영진 지분·경쟁 포지션 등) 정리 중…");
    await delay(1600);
    pushStep("종합 점수 산출·버핏 코멘트 작성 중…");
    await delay(1400);
    const sc = buffettScore100(r);
    const v = sc != null ? buffettRating(sc) : null;
    sheet.append([{ kind: "title", cells: [`[종합] ${sc ?? "–"}/100 — ${v?.rating || ""}`] }]);
    if (r.investmentSummary) {
      sheet.append([{ kind: "note", cells: [`버핏 코멘트: ${String(r.investmentSummary).slice(0, 200)}`] }]);
    }
    labStopHeartbeat("bf");
    labSet("bf.done", true);
    sheet.clearProg();   // 완료 후 조서에서 '진행' 중간 행 제거 — 채점표만 남긴다
    markUrl(sel, r);
    saveRecent(sel, r);
    setResult(r);
    setRunning(false);
  };

  // 결과 화면의 주소 — 문서함/공유에서 이 URL 로 돌아오면 캐시로 즉시 복원된다
  const deepLink = (sel: Sel, asof?: string) =>
    `/buffett?symbol=${encodeURIComponent(sel.symbol)}&market=${sel.market}&name=${encodeURIComponent(sel.name)}`
    + (asof ? `&asof=${asof}` : "");

  // 생성일이 URL 에 들어간다 — 캐시 수명이 지난 뒤 같은 회사를 다시 조회하면
  // 재분석되어 새 날짜의 URL 이 생기고, 이 URL 은 그날의 스냅샷을 영구 복원한다.
  // 대상은 인자로 받는다 — 딥링크 진입에서는 picked 상태가 아직 이전 렌더 값이다.
  const markUrl = (sel: Sel, r?: BuffettAnalysis | null) => {
    window.history.replaceState(null, "", deepLink(sel, analysisDate(r)));
  };

  // 좌측 "저장된 분석" 목록에 남긴다 — 분석일을 함께 저장해, 다시 누르면
  // 그날의 스냅샷을 그대로 편다 (openSaved)
  const saveRecent = (sel: Sel, r: BuffettAnalysis) => {
    const sc = buffettScore100(r);
    if (sc == null) return;
    recordBuffettRecent({
      name: sel.name, symbol: sel.symbol, market: sel.market,
      score: sc, rating: buffettSummary(r)?.rating || buffettRating(sc).rating,
      asof: analysisDate(r),
    });
    setSaved(buffettRecentList());
  };

  const finishWithResult = (r: BuffettAnalysis, sel: Sel) => {
    const sc = buffettScore100(r);
    const v = sc != null ? buffettRating(sc) : null;
    sheet.append(buffettFinalLines(r.moatAssessment || {}, MOAT_LABELS, sc, v?.rating || ""));
    if (r.investmentSummary) {
      sheet.append([{ kind: "note", cells: [`버핏 코멘트: ${String(r.investmentSummary).slice(0, 200)}`] }]);
    }
    labStopHeartbeat("bf");
    labSet("bf.done", true);   // 사이드바 완료 점 — 페이지를 보고 있으면 effect 가 바로 끈다
    sheet.clearProg();
    markUrl(sel, r);
    saveRecent(sel, r);
    setResult(r);
    setRunning(false);
  };

  // 실행은 항상 '드롭다운/목록으로 선택된 기업'만 — 타이핑만으로는 실행되지 않는다
  const run = async (opts?: { sel?: Sel; instant?: boolean; force?: boolean }) => {
    const sel = opts?.sel || picked;
    if (!sel) { setErr("검색 목록에서 기업을 먼저 선택하세요."); return; }
    setQuery(sel.name);
    setErr(""); setResult(null); setPdfUrl(""); setPdfKey(""); setPdfOpen(false); setSteps([]);
    sheet.reset();
    sheet.append([{ kind: "title", cells: [`버핏스코어 검토 조서 — ${sel.name}`] }]);
    const t0 = Date.now();
    const myRun = ++runIdRef.current;
    const stale = () => runIdRef.current !== myRun;
    setRunning(true); setStartedAt(t0); setElapsed("00:00");
    labHeartbeat("bf", t0, (line) => pushStep(line));
    try {
      let us10k: string | undefined;
      if (sel.market === "us") {
        pushStep("SEC 10-K 사업내용·매출구조 조회 중…");
        const enrich = await buffettUs10k(sel.symbol);
        if (stale()) return;
        if (enrich?.context_text) us10k = enrich.context_text;
        else if (enrich?.business_description) {
          us10k = `## SEC Form 10-K — Business Description\n${enrich.business_description}\n\n## Revenue Structure\n${enrich.revenue_structure || ""}`;
        }
        pushStep(us10k ? "10-K 확보 — 정성 분석에 반영합니다" : "10-K 없음 — 기본 정보로 분석합니다");
      }
      pushStep(opts?.force
        ? "저장된 분석을 무시하고 새로 분석합니다 (해자·경영진·장기 전망)…"
        : "버핏 정성 분석 시작 (해자·경영진·장기 전망)…");
      const started = await buffettStart({
        company_name: sel.name,
        symbol: sel.symbol.replace(/^KRX:/, ""),
        market: sel.market,
        us_10k_context: us10k,
        force: opts?.force || undefined,
      });
      if (stale()) {
        // 중단 뒤에 응답이 왔다 — 서버에도 알려 캐시에 남지 않게 한다
        if (started.task_id) void buffettCancel(started.task_id).catch(() => undefined);
        return;
      }
      if (started.result) {
        // 서버 캐시 적중 — 새 실행은 조서를 순서대로 재생, 문서함/딥링크 복원은 즉시
        if (opts?.instant) finishWithResult(started.result, sel);
        else void replayCached(started.result, sel, stale).catch((e) => {
          if (!(e instanceof ReplayAborted)) throw e;
        });
        return;
      }
      taskRef.current = started.task_id!;
      pollRef.current = pollTask<BuffettAnalysis>({
        statusPath: buffettStatusPath,
        taskId: started.task_id!,
        intervalMs: 3000,
        timeoutMs: 6 * 60_000,
        onStep: (step) => pushStep(step),
        resolve: (r) => finishWithResult(r, sel),
        reject: (e) => { labStopHeartbeat("bf"); labSet("bf.done", true); setErr(e.message); setRunning(false); },
      });
    } catch (e) {
      if (stale()) return;
      labStopHeartbeat("bf");
      setErr(e instanceof Error ? e.message : String(e));
      setRunning(false);
    }
  };

  /** 실행 중단 — 폴링을 끊고 서버에도 알린다(중단분은 캐시에 남기지 않는다). */
  const stop = () => {
    runIdRef.current += 1;          // 진행 중인 run() 의 이후 단계를 모두 무효화
    pollRef.current?.cancel();
    pollRef.current = null;
    labStopHeartbeat("bf");
    labSet("bf.done", true);
    setRunning(false);
    pushStep("사용자가 실행을 중단했습니다.");
    const tid = taskRef.current;
    taskRef.current = "";
    if (tid) void buffettCancel(tid).catch(() => undefined);
  };

  // 레이더 축은 MOAT_LABELS 순서로 고정한다 — 응답의 키 순서를 따르면 회사마다
  // 도형이 돌아가 비교가 안 된다. 채점되지 않은 축은 0 이 아니라 null.
  const moatAxes = useMemo(() => {
    const moat = (result?.moatAssessment || {}) as Record<string, { score?: number } | undefined>;
    return Object.entries(MOAT_LABELS).map(([k, label]) => {
      const s = moat[k]?.score;
      return { label, value: typeof s === "number" ? s : null };
    });
  }, [result]);

  const summary = useMemo(() => buffettSummary(result), [result]);
  const score = useMemo(() => buffettScore100(result), [result]);
  const verdict = summary
    ? { rating: summary.rating, action: summary.action }
    : score != null ? buffettRating(score) : null;

  // 패널 너비를 끄는 동안 루트에 .rp-resizing — 커서·가이드라인은 chat.css 가
  // .chat-root 기준으로 그린다. lab 셸의 루트가 그 .chat-root 다.
  const setRootResizing = (on: boolean) => {
    document.querySelector(".chat-root")?.classList.toggle("rp-resizing", on);
  };

  // PDF 보고서 — 우측 패널을 먼저 열고(생성 중 상태를 패널 안에서 보여준다)
  // 이미 만든 보고서가 있으면 다시 만들지 않는다
  const openPdf = async () => {
    if (!result || score == null || !picked) return;
    const key = pdfOwner(picked, result);
    setPdfOpen(true);
    if (pdfUrl && pdfKey === key) return;   // 같은 기업·같은 분석일 때만 재사용
    setPdfUrl(""); setPdfKey("");           // 다른 것이면 버리고 새로 만든다
    setPdfBusy(true); setErr("");
    try {
      const url = await buffettPdf({
        company_name: picked.name,
        symbol: picked.symbol.replace(/^KRX:/, ""),
        score_100: score,
        rating: verdict?.rating || "",
        action: verdict?.action || "",
        summary: result.investmentSummary || "",
        analysis: result,
      });
      setPdfUrl(url); setPdfKey(key);   // 우측 패널이 미리보기로 렌더 — 새 창 강제 오픈은 하지 않는다
      // 문서함에도 채팅 산출물과 같은 방식으로 쌓는다
      recordLabFile({
        url, kind: "report", feature: "버핏스코어",
        page: deepLink(picked, analysisDate(result)),
        company: picked.name,
        label: decodeURIComponent((url.split("?")[0].split("/").pop() || "")) || `${picked.name}_버핏스코어_보고서.pdf`,
      });
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setPdfBusy(false);
    }
  };

  // ── 화면 상태 ───────────────────────────────────────────────────
  const view: "land" | "busy" | "report" =
    running ? "busy" : result && score != null ? "report" : "land";

  /** 모바일 목록 시트 — ≤860px 에선 좌측(검색·저장된 분석)이 화면에 없다.
   *  우상단 '목록' 버튼으로 아래에서 올라오는 시트로 연다(시장 이슈와 같은 패턴).
   *  데스크톱에선 CSS 가 버튼·딤을 숨기고 aside 는 평소 자리로 돌아간다. */
  const [mSide, setMSide] = useState(false);

  const pick = (sel: Sel) => {
    setPicked(sel);
    setQuery(sel.name);
    setHits([]);
    setMSide(false); // 시트에서 골랐으면 결과 화면이 보여야 한다
    void run({ sel });
  };

  // 저장된 분석 = 이미 만들어진 결과다. 나이와 무관하게 **그날의 스냅샷을 그대로
  // 편다** — 새 분석은 결과 화면의 '다시 분석' 버튼이 명시적으로 건다 (예전엔
  // 7일이 지나면 클릭만으로 3분짜리 재분석이 시작돼 당황스러웠다).
  const openSaved = (s: BuffettRecent) => {
    if (running) return;
    setMSide(false); // 시트에서 골랐으면 결과 화면이 보여야 한다
    navigate(deepLink({ name: s.name, symbol: s.symbol, market: s.market }, s.asof));
  };

  // 랜딩으로 되돌아가기 — 결과를 보다가 다른 기업을 찾고 싶을 때의 유일한 출구
  const goLanding = () => {
    if (running) return;
    pollRef.current?.cancel();
    setPicked(null); setResult(null); setQuery(""); setHits([]);
    setSteps([]); setErr(""); setPdfUrl(""); setPdfKey(""); setPdfOpen(false);
    navigate("/buffett");
  };

  // 검색 실행 — 선택된 기업이 있으면 그대로, 없으면 첫 후보를 골라 바로 실행한다
  const submit = () => {
    if (running) return;
    if (picked) { void run(); return; }
    if (hits.length) { pick(toSel(hits[0])); return; }
    setErr("검색 목록에서 기업을 먼저 선택하세요.");
  };

  const savedSymbols = useMemo(() => new Set(saved.map((s) => s.symbol)), [saved]);

  // 진행 로그 — 분석 중에는 마지막 줄을 강조하고, 끝난 뒤에는 평평하게 편다
  const logRows = (live: boolean) => (
    <div className="bfx-log">
      {steps.map((s, i) => (
        <div key={i} className={`bfx-log-row${live && i === steps.length - 1 ? " now" : ""}`}>
          <span className="mk">{live && i === steps.length - 1 ? "▸" : "•"}</span>
          <span className="tx">{s}</span>
        </div>
      ))}
    </div>
  );

  const searchBox = (variant: "side" | "hero") => (
    <div className={`bfx-search${variant === "hero" ? " hero" : ""}`}>
      <div className="bfx-search-row">
        {variant === "hero" && (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="7" /><path d="M20 20l-3.4-3.4" />
          </svg>
        )}
        <input
          // 검색창이 두 곳(첫 화면·좌측 패널)인데 상태는 하나 — 값까지 둘 다에
          // 그리면 한쪽에 치는 글자가 다른 쪽에 실시간 미러링돼 이상하게 보인다
          // (사용자 제보 2026-08-20). 입력 중인 창에만 표시한다.
          value={typing === variant ? query : ""}
          placeholder={variant === "hero" ? "기업명이나 종목코드를 입력하세요" : "삼성전자, 005930, AAPL"}
          onChange={(e) => { setTyping(variant); setQuery(e.target.value); setPicked(null); }}
          onFocus={() => setTyping(variant)}
          onKeyDown={(e) => {
            if (e.nativeEvent.isComposing || e.keyCode === 229) return; // 한글 IME 조합 중 실행 안 함
            if (e.key === "Enter") submit();
          }}
          disabled={running}
        />
        <button type="button" className={running ? "bfx-run is-stop" : "bfx-run"}
                onClick={running ? stop : submit}
                disabled={!running && !picked && hits.length === 0}>
          {running ? "실행 중단" : "분석 실행"}
        </button>
      </div>
      {(hits.length > 0 || searching) && typing === variant && (
        <div className="bfx-pop">
          {searching && hits.length === 0
            // 찾는 동안 결과 줄의 자리를 그대로 잡아 둔다 — 팝오버가 나타났다
            // 사라졌다 하지 않고, 몇 줄이 올지 미리 보인다
            ? [0, 1, 2].map((i) => (
              <div key={i} className="bfx-pop-item skel" aria-hidden="true">
                <span className="sk sk-logo" />
                <span className="sk-lines">
                  <span className="sk sk-name" />
                  <span className="sk sk-sym" />
                </span>
              </div>
            ))
            : hits.map((h) => {
              const sel = toSel(h);
              return (
                <button key={h.symbol} type="button" className="bfx-pop-item" onClick={() => pick(sel)}>
                  <CompanyLogo symbol={h.symbol} name={sel.name} size="sm" />
                  <span className="nm">
                    <b>{sel.name}</b>
                    <span>{h.symbol}</span>
                  </span>
                </button>
              );
            })}
        </div>
      )}
    </div>
  );

  return (
    <div className={`bfx-shell${pdfOpen ? " with-pdf" : ""}${mSide ? " m-side" : ""}`}>
      <div className="bfx-page">
      {/* 모바일 — 좌측 목록을 여는 버튼 + 시트 뒤 딤 (데스크톱에선 CSS 가 숨긴다) */}
      <button type="button" className="bfx-side-open" onClick={() => setMSide(true)}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
          <path d="M8 6h13" /><path d="M8 12h13" /><path d="M8 18h13" />
          <path d="M3 6h.01" /><path d="M3 12h.01" /><path d="M3 18h.01" />
        </svg>
        목록
      </button>
      <SheetDim className="bfx-dim" onClose={() => setMSide(false)} />
      <aside className="bfx-side">
        <SheetGrabber className="bfx-grab" sheetSelector=".bfx-side" onClose={() => setMSide(false)} />
        <div className="bfx-side-head">
          <div className="bfx-titlerow">
            <strong>버핏 스코어</strong>
            {view !== "land" && (
              <button type="button" className="bfx-home" onClick={goLanding} disabled={running}>첫 화면</button>
            )}
          </div>
          <p>한 번에 한 종목씩, 경제적 해자와 경영진·장기 전망을 버핏의 기준으로 채점합니다.</p>
        </div>

        {/* 시안은 랜딩에서 좌측 검색을 숨겼지만(히어로 검색과 중복), 어느 상태에서든
            검색 자리가 같은 곳에 있어야 한다 — 사용자 지시(2026-08-14) */}
        <div className="bfx-side-search">
          <div className="bfx-label">기업 검색</div>
          {searchBox("side")}
          <div className="bfx-note">분석에는 3분 이상 걸릴 수 있습니다. 최근 7일 안에 분석한 종목은 저장된 결과를 바로 보여줍니다.</div>
        </div>

        <div className="bfx-side-listhead">
          <span className="bfx-label">저장된 분석</span>
          <span>{saved.length}건</span>
        </div>
        <div className="bfx-saved">
          {saved.length === 0 ? (
            <div className="bfx-saved-empty">아직 분석한 종목이 없습니다.<br />기업을 검색해 분석을 실행하세요.</div>
          ) : saved.map((s) => (
            <button key={s.symbol} type="button"
              className={`bfx-saved-item${picked?.symbol === s.symbol ? " on" : ""}`}
              onClick={() => openSaved(s)}>
              <CompanyLogo symbol={s.symbol} name={s.name} size="md" />
              <span className="nm">
                <b>{s.name}</b>
                <span>{s.asof} 분석 · {s.rating}</span>
              </span>
              <span className={`sc ${scoreClass(s.score)}`}>{s.score}</span>
            </button>
          ))}
        </div>
      </aside>

      <div className="bfx-body">
        {view === "land" && (
          <div className="bfx-land">
            <div className="bfx-wrap">
              <div className="bfx-hero">
                <div>
                  <div className="bfx-hero-eyebrow">BUFFETT SCORE</div>
                  <h1>워런 버핏이 이 회사를 본다면</h1>
                  <p>사업보고서 본문과 최근 5개년 재무를 읽어 경제적 해자와 경영진, 장기 전망을 채점합니다. 숫자 하나가 아니라 그가 쓸 법한 검토 조서 한 장으로 돌려드립니다.</p>
                </div>
                <img className="bfx-portrait" src={portrait} alt="" />
              </div>
              {searchBox("hero")}
              <div className="bfx-quick">
                {QUICK.map((c) => (
                  <Chip key={c.symbol} disabled={running} onClick={() => pick(c)}>{c.name}</Chip>
                ))}
              </div>
              {err && <div className="bfx-err" role="alert">{err}</div>}
            </div>

            <div className="bfx-wrap sec">
              <div className="bfx-h2">채점 방식</div>
              <div className="bfx-how">
                {HOW.map((h) => (
                  <div className="bfx-how-card" key={h.t}>
                    <div className="hd">
                      <span>{h.t}</span>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">{h.icon}</svg>
                    </div>
                    <p>{h.s}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="bfx-wrap last">
              <div className="bfx-h2">버크셔 해서웨이가 보유한 기업</div>
              <div className="bfx-h2-sub">2026년 2분기 13F 기준 · 포트폴리오 비중순</div>
              <div className="bfx-berk">
                {BERKSHIRE.map((b) => {
                  const ticker = b.ticker.split(":")[1];
                  const done = savedSymbols.has(ticker);
                  return (
                    <button key={b.ticker} type="button" className="bfx-berk-item"
                      onClick={() => pick({ name: b.name, symbol: ticker, market: "us" })}>
                      <span className="ln">
                        <CompanyLogo symbol={b.ticker} name={b.name} size="md" />
                        <b>{b.name}</b>
                        <span>{b.ticker}</span>
                        <span>{b.since}부터</span>
                        <span className="sp" />
                        <span className={`tag${done ? " done" : ""}`}>{done ? "분석 완료" : "분석하기"}</span>
                      </span>
                      <span className="why">{b.why}</span>
                    </button>
                  );
                })}
              </div>
              <div className="bfx-foot">목록의 기업을 누르면 같은 기준으로 채점한 검토 조서를 볼 수 있습니다. 다른 기업은 왼쪽에서 검색해 분석을 실행하세요.</div>
            </div>
          </div>
        )}

        {view === "busy" && (
          <div className="bfx-sheetwrap">
            <div className="bfx-sheet">
              <div className="bfx-sheet-head">
                <span className="who">
                  <strong>{picked?.name}</strong>
                  <span>{picked?.symbol}</span>
                  <button type="button" className="bfx-rerun" disabled={running}
                    title="최근 7일 내 분석이 있으면 그 결과를 다시 펴고, 지났으면 새로 분석합니다 (3분 이상)"
                    onClick={() => { if (picked) void run({ sel: picked }); }}>
                    다시 분석
                  </button>
                </span>
                <span className="asof">{elapsed} 경과</span>
              </div>
              <div className="bfx-busy-sub">분석 중 · 대상에 따라 3분 이상 걸릴 수 있습니다</div>

              <div className="bfx-block">
                <div className="bfx-label">분석 로그</div>
                {logRows(true)}
              </div>

              {err && <div className="bfx-err" role="alert">{err}</div>}
              <LiveSheet lines={sheet.lines} running={running} />
            </div>
          </div>
        )}

        {view === "report" && result && score != null && (
          <div className="bfx-sheetwrap">
            <div className="bfx-sheet">
              <div className="bfx-sheet-head">
                <span className="who">
                  <strong>{picked?.name}</strong>
                  <span>{picked?.symbol}</span>
                  <button type="button" className="bfx-rerun" disabled={running}
                    title="최근 7일 내 분석이 있으면 그 결과를 다시 펴고, 지났으면 새로 분석합니다 (3분 이상)"
                    onClick={() => { if (picked) void run({ sel: picked }); }}>
                    다시 분석
                  </button>
                </span>
                <span className="asof">
                  {analysisDate(result)} 기준 · {picked?.market === "us" ? "SEC 10-K" : "사업보고서"}
                  {/* 조서가 길어 하단 버튼이 스크롤 밖이다 — 머리에도 같은 출구를 둔다 */}
                  <button type="button" className="bfx-pdf-btn" disabled={pdfBusy} onClick={() => void openPdf()}>
                    {pdfBusy ? "생성 중…" : "PDF 보고서"}
                  </button>
                </span>
              </div>

              {/* 분석하면서 나온 것들 — 결과만 남기고 지우면 어떻게 나온 점수인지
                  확인할 길이 없다. 조서 머리에 접어 두고 필요할 때 편다 */}
              {(steps.length > 0 || sheet.lines.length > 0) && (
                <div className="bfx-folds">
                  {steps.length > 0 && (
                    <details className="bfx-fold">
                      <summary>분석 로그 <span>{steps.length}건</span></summary>
                      <div className="bfx-fold-body">{logRows(false)}</div>
                    </details>
                  )}
                  {sheet.lines.length > 0 && (
                    <details className="bfx-fold">
                      <summary>검토 조서 <span>작성 완료</span></summary>
                      <div className="bfx-fold-body">
                        <LiveSheet lines={sheet.lines} running={false} />
                      </div>
                    </details>
                  )}
                </div>
              )}

              {/* 점수·판정·코멘트는 한 판단이다 — 떼어 놓으면 결론을 두 번 읽어야 한다 */}
              <div className="bfx-verdict">
                <div className="num">
                  <b className={scoreClass(score)}>{score}</b>
                  <span className={scoreClass(score)}>{verdict?.rating} · 100점 만점</span>
                  {/* 초상을 점수 아래(왼쪽 칸)로 — 코멘트 옆에 두면 본문이 그만큼
                      좁아진다. 점수·등급·인물이 한 덩어리로 읽히는 편이 낫다 */}
                  {result.investmentSummary ? (
                    <img className="face"
                      src={buffettPortrait(score, picked ? pdfOwner(picked, result) : String(score))} alt="" />
                  ) : null}
                </div>
                <div className="say">
                  <p className="act">{verdict?.action}</p>
                  {result.investmentSummary ? (
                    <div className="cmt">
                      <div className="bfx-label">버핏 코멘트</div>
                      <p>{String(result.investmentSummary)}</p>
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="bfx-block">
                <div className="bfx-label">
                  경제적 해자 6대 축
                  {summary?.missingMoats?.length ? ` · ${summary.moatCount}/6축 평가` : ""}
                </div>
                {/* 도형은 강약을 한눈에, 아래 표는 점수와 근거를 정확히 — 둘은 한 벌이다 */}
                <div className="bfx-radar">
                  <RadarChart axes={moatAxes} max={10} />
                </div>
                <div>
                  {Object.entries(MOAT_LABELS).map(([k, label]) => {
                    const m = (result.moatAssessment || {})[k];
                    if (!m) return null;
                    return (
                      <div className="bfx-moat-row" key={k}>
                        <span className="lb">{label}</span>
                        <span className={`vl ${typeof m.score === "number" ? scoreClass(m.score * 10) : "bfx-bad"}`}>
                          {typeof m.score === "number" ? `${m.score}/10` : "–"}
                        </span>
                        <span className="ev">{m.evidence || ""}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {summary?.gates?.length ? (
                <div className="bfx-block">
                  <div className="bfx-label">7대 투자 게이트 · {summary.gatesPassed}/7 통과</div>
                  <div className="bfx-gates">
                    {summary.gates.map((g) => (
                      <div className="bfx-gate" key={g.key}>
                        <span className={`mk${g.passed === true ? " ok" : g.passed === false ? " no" : ""}`}>
                          {g.passed === true ? "✓" : g.passed === false ? "✗" : "–"}
                        </span>
                        <span className="tx">
                          <b>{g.label}</b>
                          <span>{g.basis}</span>
                        </span>
                      </div>
                    ))}
                    {summary.gates.length % 2 === 1 && <div className="bfx-gate-fill" />}
                  </div>
                </div>
              ) : null}

              <div className="bfx-block">
                <div className="bfx-label">정성 분석 상세</div>
                <div>
                  {Object.entries(SECTION_LABELS).map(([key, label]) => {
                    const sec = result[key] as Record<string, unknown> | undefined;
                    if (!sec?.answer) return null;
                    // 보조 필드(riskLevel·stability 같은 영문 키:값)는 싣지 않는다 —
                    // 내부 스키마 노출이고 본문이 이미 같은 내용을 문장으로 말한다.
                    return (
                      <div className="bfx-qual-row" key={key}>
                        <span className="lb">{label}</span>
                        <span className="bd">{String(sec.answer)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {summary?.missingMoats?.length ? (
                <div className="bfx-block">
                  <div className="bfx-label">확인이 필요한 지점</div>
                  <div>
                    {summary.missingMoats.map((k) => (
                      <div className="bfx-open-row" key={k}>
                        {MOAT_LABELS[k] || k} 축은 이번 분석에서 채점되지 않았습니다 — 근거로 삼을 자료가 부족했습니다.
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {err && <div className="bfx-err" role="alert">{err}</div>}

              <div className="bfx-cta">
                <button type="button" className="sub" disabled={pdfBusy} onClick={() => void openPdf()}>
                  {pdfBusy ? "생성 중…" : "PDF 보고서"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      </div>

      {/* PDF 미리보기 — 채팅의 결과·파일 패널과 같은 프레임(ui/SidePanel).
          조서를 밀어내지 않고 오른쪽에 나란히 선다 */}
      {pdfOpen && (
        <SidePanel
          label="PDF 보고서 미리보기"
          className="bfx-pdf-panel"
          onResizingChange={setRootResizing}
          titles={
            <>
              <div className="rp-title">PDF 보고서</div>
              <div className="rp-sub">{picked?.name}{picked?.symbol ? ` · ${picked.symbol}` : ""}</div>
            </>
          }
          actions={
            <>
              {pdfUrl && (
                <>
                  {/* S3 는 크로스오리진이라 download 속성이 무시된다 — blob 저장(문서함과 동일) */}
                  <button className="lab-chip" type="button"
                    onClick={() => void libDownload(pdfUrl, `${picked?.name || "버핏스코어"}_보고서.pdf`)}>
                    다운로드 ↓
                  </button>
                  <a className="lab-chip" href={libViewUrl(pdfUrl)} target="_blank" rel="noreferrer">새 창 ↗</a>
                </>
              )}
              <PanelClose label="PDF 보고서 닫기" onClick={() => setPdfOpen(false)} />
            </>
          }
        >
          <div className="rp-body">
            {pdfUrl
              // inline 프록시(report-view)라 다운로드로 튕기지 않고 그대로 렌더된다
              ? <iframe src={libViewUrl(pdfUrl)} title="버핏스코어 PDF 미리보기" />
              : <div className="rp-empty">{pdfBusy ? "보고서를 만드는 중입니다…" : "보고서를 준비하지 못했습니다."}</div>}
          </div>
        </SidePanel>
      )}
    </div>
  );
}
